import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { BigNumber, utils } from 'ethers';
import { sha256 } from 'ethers/lib/utils';
import ModalToChainTimeout from '../../ModalToChainTimeout';
import ModalTransactionComplete from '../../ModalTransactionComplete';
import ModalTransactionFailed from '../../ModalTransactionFailed';
import { findChainBySymbol } from '../../../datas/chains';
import {
  selectBridgeSlice,
  setTxCurrentHistory,
  setTxHasError,
  setIsConfirm,
  setIsApproval,
} from '../../../redux/slices/bridgeSlice';
import {
  useAppDispatch,
  useAppSelector,
  useFromChainBalance,
  useFromChainWalletAddress,
  useInterval,
  useNotification,
  useToChainWalletAddress,
  useTransactionProgress,
} from '../../../hooks';
import HEXLANT from '../../../utils/hexlant';
import BridgeBackend from '../../../services/bridge-backend.service';
import {
  resetSessionExpiredAt,
  selectWalletSlice,
} from '../../../redux/slices/walletSlice';
import ModalWithdrawApproval from '../../ModalWithdrawApproval';
import { generatePreimage, generateTimelock } from '../../../utils/htlc';
import { setModal } from '../../../redux/slices/modalSlice';
import ModalNewContractApproval from '../../ModalNewContractApproval';
import buildHtlcService from '../../../utils/buildHtlcService';
import ModalApproval from '../../ModalApproval';
import ModalApprovalFailed from '../../ModalApprovalFailed';
import OTPProvider from '../../../providers/OTPProvider';
import { OTPModalType } from '../../../hooks/mutates/useOTP';
import { removeDisposableTokenTokenInLocalStorage } from '../../../storage/token';

const NOTIFICATION_MSG = `To receive asset, confirmation is required. Proceed confirmation in your recipient Wallet.`;

const TransactionProgress: FC = () => {
  const dispatch = useAppDispatch();
  const notificationAction = useNotification();
  const {
    selectedFromChain,
    selectedToChain,
    toChainHtlc,
    fromChainHtlc,
    txCurrentHistory,
    txHasError,
    selectedToken,
    tokenTransferAmount,
    fromChainToken,
    toChainToken,
    isConfirm,
    isApproval,
    txIsOngoing,
  } = useAppSelector(selectBridgeSlice);
  const { fromChainWalletProvider, toChainWalletProvider } =
    useAppSelector(selectWalletSlice);
  const fromChainBalance = useFromChainBalance();
  const [notificationSetInterval, setNotificationSetInterval] = useState(false);
  const [isDesktopAlarmNotified, setIsDesktopAlarmNotified] = useState(true);
  const [isShowWithdrawApproval, setIsShowWithdrawApproval] = useState(false);
  const [isShowNewContractApproval, setIsShowNewContractApproval] =
    useState(false);
  const [isEthWithdraw, setIsEthWithdraw] = useState(true);
  const [isShowModalApproval, setIsShowModalApproval] = useState(false);
  const [isShowModalApprovalFailed, setIsShowModalApprovalFailed] =
    useState(false);
  const [isInterval, setIsInterval] = useState(true);
  const [OTPModal, setOTPModal] = useState<OTPModalType | null>(null);

  const fromChainWalletAddress = useFromChainWalletAddress();
  const toChainWalletAddress = useToChainWalletAddress();
  const fromTransactionHash = txCurrentHistory?.fromTxHash;
  const toTransactionHash = txCurrentHistory?.toTxHash;
  const transactionProgress = useTransactionProgress();
  const [approvalInProgress, setApprovalInProgress] = useState(false);
  const fromCurrentTimelock = useMemo(
    () =>
      txCurrentHistory
        ? dayjs(Number(txCurrentHistory.fromChainTimelock) * 1000)
            .utc()
            .format(`YYYY-MM-DD HH:mm:ss`)
        : ``,
    [txCurrentHistory],
  );
  const toCurrentTimelock = useMemo(
    () =>
      txCurrentHistory?.toChainTimelock
        ? dayjs(Number(txCurrentHistory.toChainTimelock) * 1000)
            .utc()
            .format(`YYYY-MM-DD HH:mm:ss`)
        : ``,
    [txCurrentHistory],
  );

  const fromChain = useMemo(
    () =>
      txCurrentHistory?.fromChainSymbol &&
      findChainBySymbol(txCurrentHistory?.fromChainSymbol),
    [txCurrentHistory?.fromChainSymbol],
  );
  const toChain = useMemo(
    () =>
      txCurrentHistory?.toChainSymbol &&
      findChainBySymbol(txCurrentHistory?.toChainSymbol),
    [txCurrentHistory?.toChainSymbol],
  );

  useInterval(
    async () => {
      if (!txCurrentHistory) return;

      try {
        const history = await BridgeBackend.getHistory(txCurrentHistory.id);
        dispatch(
          setTxCurrentHistory({
            ...txCurrentHistory,
            ...history,
            // 로컬에서 유지 및 관리하는 변수들
            preimg: txCurrentHistory.preimg,
          }),
        );

        if (history.toChainHtlcState === `withdrawn`) {
          setIsInterval(false);
        }
      } catch (error) {
        console.error(error);
      }
    },
    isInterval && txCurrentHistory ? 5000 : null,
  );

  useEffect(() => {
    if (txCurrentHistory?.toChainHtlcState === `mirrored`) {
      // interval 실행전 최초 confirm버튼 활성화시 노출되는 데스크탑 알림
      if (isDesktopAlarmNotified) {
        notificationAction(NOTIFICATION_MSG);
        setIsDesktopAlarmNotified(false);
      }
    }
  }, [
    dispatch,
    isDesktopAlarmNotified,
    notificationAction,
    notificationSetInterval,
    toCurrentTimelock,
    txCurrentHistory?.toChainHtlcState,
    txCurrentHistory?.toTxHash,
  ]);

  useEffect(() => {
    if (
      txCurrentHistory?.fromChainSymbol === `KS_BESU` &&
      txCurrentHistory?.fromChainHtlcState === `newContract`
    ) {
      dispatch(setIsConfirm(false));
    }
  }, [
    dispatch,
    selectedFromChain?.symbol,
    txCurrentHistory?.fromChainHtlcState,
    txCurrentHistory?.fromChainSymbol,
    txIsOngoing,
  ]);

  useEffect(() => {
    if (txCurrentHistory?.fromChainHtlcState === `withdrawn`) {
      dispatch(setIsConfirm(false));
    }
  }, [dispatch, txCurrentHistory?.fromChainHtlcState]);

  // ETH => 그냥 실행 / KS_BESU => 컨펌버튼 클릭시 (newContract) 생성
  const onRequestConfirmNewContract = useCallback(async () => {
    if (!toChainHtlc) {
      console.error(`HTLC not supported chain`);
      return;
    }
    if (!toChainWalletAddress) {
      console.error(`No receiver address error`);
      return;
    }

    if (
      !tokenTransferAmount ||
      !fromChainToken ||
      !fromChainWalletAddress ||
      !selectedFromChain ||
      !selectedToChain ||
      !selectedToken ||
      !toChainWalletAddress ||
      !fromChainHtlc ||
      !fromChainBalance
    ) {
      console.error(`tokenTransferAmount : `, tokenTransferAmount);
      console.error(`fromChainToken : `, fromChainToken);
      console.error(`fromChainWalletAddress : `, fromChainWalletAddress);
      console.error(`selectedFromChain : `, selectedFromChain);
      console.error(`selectedToChain : `, selectedToChain);
      console.error(`selectedToken : `, selectedToken);
      console.error(`toChainWalletAddress : `, toChainWalletAddress);
      console.error(`fromChainHtlc : `, fromChainHtlc);
      console.error(`fromChainBalance :`, fromChainBalance);
      window.location.replace(`/404error`);
      return;
      // throw new Error(`Insufficient Parameters`);
    }

    const amount = utils
      .parseUnits(tokenTransferAmount!, fromChainToken.decimals)
      .toString();

    const timeoutIdNewContract = setTimeout(() => {
      dispatch(setModal(`TimeoutLogout`));
    }, 3600000);

    // 2. 지갑 승인 성공시, 서버에 History 저장
    const preimage = generatePreimage();
    const hashlock = sha256(preimage);
    const timelock = generateTimelock();
    const preimageHex = `0x${Buffer.from(preimage).toString(`hex`)}`;
    const historyData = {
      fromChainSymbol: selectedFromChain.symbol,
      toChainSymbol: selectedToChain.symbol,
      tokenSymbol: fromChainToken.symbol,
      sender: fromChainWalletAddress,
      receiver: toChainWalletAddress,
      fromChainTimelock: timelock.toString(),
      hashlock,
      amount: BigNumber.from(amount).toString(),
    };
    const { id: historyId } = await BridgeBackend.postHistory(historyData);
    dispatch(
      setTxCurrentHistory({
        id: historyId,
        fromEvmChainId: 0,
        toEvmChainId: 0,
        preimg: preimageHex,
        fromChainHtlcState: null,
        toChainHtlcState: null,
        createdAt: dayjs().utc().toISOString(),
        toChainTimelock: null,
        fromTokenAddress: null,
        toTokenAddress: null,
        fromTxHash: null,
        toTxHash: null,
        ...historyData,
      }),
    );
    // preimg 스토리지에 저장
    const oldMap = JSON.parse(localStorage.getItem(`preimgHashMap`) || `{}`);
    const preimgHashMap = {
      ...oldMap,
      [hashlock]: preimageHex,
    };
    localStorage.setItem(`preimgHashMap`, JSON.stringify(preimgHashMap));
    // let hexlantTransferAmount = Number(tokenTransferAmount);
    // const currentBalance = utils.formatUnits(
    //   fromChainBalance,
    //   fromChainToken?.decimals || undefined,
    // );

    // if (Number(currentBalance).toFixed(2) === tokenTransferAmount)
    //   hexlantTransferAmount = Number(tokenTransferAmount) - 1;

    try {
      const hexlantNewContract = fromChainHtlc.isNative
        ? HEXLANT.hexlantNewContract
        : HEXLANT.hexlantNewContractToken;
      const {
        data: { transactionHash },
      } = await hexlantNewContract(
        tokenTransferAmount,
        selectedToChain.evmChainId.toString(),
        hashlock,
        toChainWalletAddress,
        timelock.toString(),
        fromChainHtlc.address,
        fromChainToken.address,
      );

      if (transactionHash) {
        notificationAction(NOTIFICATION_MSG);
      }
    } catch (e) {
      await BridgeBackend.reject(historyId);
      dispatch(setTxHasError(true));
    }
    removeDisposableTokenTokenInLocalStorage();
    clearTimeout(timeoutIdNewContract);
  }, [
    dispatch,
    fromChainBalance,
    fromChainHtlc,
    fromChainToken,
    fromChainWalletAddress,
    notificationAction,
    selectedFromChain,
    selectedToChain,
    selectedToken,
    toChainHtlc,
    toChainWalletAddress,
    tokenTransferAmount,
  ]);

  const gndChainApproval = useCallback(async () => {
    if (!tokenTransferAmount || !fromChainToken) {
      console.error(`tokenTransferAmount : `, tokenTransferAmount);
      console.error(`fromChainToken : `, fromChainToken);
      return;
    }
    // NOTE: isNative, allowance check -> appoveErc20
    try {
      setApprovalInProgress(true);

      if (fromChainHtlc && !fromChainHtlc.isNative) {
        const allowance = await HEXLANT.hexlantAllowance(
          fromChainToken.address,
          fromChainHtlc.address,
        );

        if (
          Number(tokenTransferAmount) > Number(allowance.data.allowanceValue)
        ) {
          await HEXLANT.hexlantApprove(
            tokenTransferAmount,
            fromChainToken.address,
            fromChainHtlc.address,
          );
        }
      }

      setApprovalInProgress(false);
      dispatch(setIsApproval(true));
    } catch (e) {
      setIsShowModalApprovalFailed(true);
    }
  }, [dispatch, tokenTransferAmount, fromChainToken, fromChainHtlc]);

  // 버튼 (withdraw)
  const onRequestConfirmWithdraw = useCallback(async () => {
    if (txCurrentHistory?.toChainHtlcState !== `mirrored`) {
      console.error(`To chain not mirrored yet`);
      return;
    }
    if (!toChainHtlc) {
      console.error(`HTLC not supported chain`);
      return;
    }
    if (!txCurrentHistory?.hashlock) {
      console.error(`No contract id error`);
      return;
    }
    if (selectedFromChain?.symbol === `ETH`) {
      try {
        const {
          data: { transactionHash },
        } = await HEXLANT.hexlantWithdraw(
          toChainHtlc.address,
          txCurrentHistory.hashlock,
          txCurrentHistory.preimg,
        );
        if (transactionHash) {
          notificationAction(NOTIFICATION_MSG);
        }
        dispatch(
          setTxCurrentHistory({
            ...txCurrentHistory,
            toTxHash: transactionHash,
          }),
        );
      } catch (e) {
        dispatch(setIsConfirm(false));
        dispatch(setTxHasError(true));
      }
    }
    if (selectedFromChain?.symbol === `KS_BESU`) {
      const htlcService = await buildHtlcService(
        toChainWalletProvider,
        selectedToChain,
        toChainToken,
        toChainHtlc,
      );
      try {
        const { tx: withdrawTxHash } = await htlcService.withdraw(
          txCurrentHistory.hashlock,
          txCurrentHistory.preimg,
        );
        if (withdrawTxHash) {
          notificationAction(NOTIFICATION_MSG);
        }
        dispatch(
          setTxCurrentHistory({
            ...txCurrentHistory,
            toTxHash: withdrawTxHash.toString(),
          }),
        );
      } catch (e) {
        console.error(e);
        dispatch(setIsConfirm(false));
        // setIsEthWithdraw(false);
        dispatch(setTxHasError(true));
      }
    }
  }, [
    dispatch,
    notificationAction,
    selectedFromChain?.symbol,
    selectedToChain,
    toChainHtlc,
    toChainToken,
    toChainWalletProvider,
    txCurrentHistory,
  ]);

  const disabledFromBtn = useMemo(() => {
    if (!isApproval) return approvalInProgress;
    if (!fromChainBalance) return true;

    return isConfirm;
  }, [isApproval, isConfirm, approvalInProgress, fromChainBalance]);

  // 사용자가 동작할 시 resetSessionExpiredAt의 시간을 리셋
  // dependency는 input의 값들이 변경될때마다 실행되어야 하기 때문에 추가 하였습니다.
  useEffect(() => {
    dispatch(resetSessionExpiredAt());
  }, [
    dispatch,
    txCurrentHistory?.toChainHtlcState,
    txCurrentHistory?.fromChainHtlcState,
  ]);

  useEffect(() => {
    if (
      !isConfirm &&
      isEthWithdraw &&
      txCurrentHistory?.toChainSymbol === `ETH` &&
      txCurrentHistory?.fromChainHtlcState === `mirrored` &&
      txCurrentHistory.toChainHtlcState === `mirrored`
    ) {
      onRequestConfirmWithdraw();
      dispatch(setIsConfirm(true));
      setIsEthWithdraw(false);
    }
  }, [
    dispatch,
    isConfirm,
    isEthWithdraw,
    onRequestConfirmWithdraw,
    txCurrentHistory?.fromChainHtlcState,
    txCurrentHistory?.toChainHtlcState,
    txCurrentHistory?.toChainSymbol,
  ]);

  useEffect(() => {
    if (selectedFromChain?.symbol === `ETH`) {
      dispatch(setIsApproval(true));
    }
  }, [dispatch, selectedFromChain?.symbol]);

  return (
    <OTPProvider
      type="confirm"
      OTPModal={OTPModal}
      setOTPModal={setOTPModal}
      onSuccess={() => {
        setIsShowNewContractApproval(true);
      }}
      handleCloseModal={() => setOTPModal(null)}
    >
      <main
        className="form-exchange column col-9 wow fadeInRight"
        data-wow-delay="0.5s"
      >
        <div className="form-container">
          <div className="inner">
            <div className="headline">
              <i data-icon="process" />
              <p>
                4th step! Conversion is almost completed. Please wait a moment
              </p>
            </div>
            {txHasError ? (
              <div data-caution="danger" style={{ margin: `60px 0 0 0` }}>
                <i data-icon="error" />
                <strong>Transfer Failed</strong>
              </div>
            ) : (
              <div
                data-caution={
                  transactionProgress.status === `success` ? `success` : `info`
                }
                style={{ margin: `60px 0 0 0` }}
              >
                <i
                  data-icon={
                    transactionProgress.status === `success`
                      ? `success`
                      : `info`
                  }
                />
                {transactionProgress.step === `4-1` &&
                  `Confirm transaction from your sending wallet.`}
                {transactionProgress.step === `4-2` &&
                  `Conversion in progress. Please stand by.`}
                {transactionProgress.step === `4-3` &&
                  transactionProgress.status === `waiting` && (
                    <>
                      <strong>Confirm transaction</strong> from your receiving
                      wallet.
                      {` `}
                      <span className="error">
                        Please maintain confirmation until {toCurrentTimelock}
                        {` `}
                        &#40;UTC&#41;.
                      </span>
                    </>
                  )}
                {transactionProgress.step === `4-3` &&
                  transactionProgress.status === `success` && (
                    <>
                      <strong>Transaction confirmed.</strong> Please monitor the
                      history until the conversion is completed.
                    </>
                  )}
              </div>
            )}
            <div className="exchange-coin" style={{ margin: `80px` }}>
              <div
                className={`step-status ${
                  transactionProgress.step === `4-1`
                    ? transactionProgress.status
                    : `success`
                } ${!isApproval && `approval`}`}
              >
                <span>From</span>
                <img
                  src={selectedFromChain?.logoUrl}
                  className="i-blockchain"
                  alt={selectedFromChain?.name}
                />
                <strong>{selectedFromChain?.name}</strong>
                {transactionProgress.step === `4-1` &&
                  selectedFromChain?.symbol === `KS_BESU` && (
                    <button
                      className="t-btn"
                      onClick={() => {
                        if (!isApproval) {
                          setIsShowModalApproval(true);
                        } else {
                          setOTPModal(`otp`);
                        }
                      }}
                      disabled={disabledFromBtn}
                    >
                      {!isApproval ? `Approve` : `Confirm`}
                    </button>
                  )}
              </div>
              <div
                className={
                  transactionProgress.step === `4-3`
                    ? `step-status ${transactionProgress.status}`
                    : ``
                }
              >
                <span>To</span>
                <img
                  src={selectedToChain?.logoUrl}
                  className="i-blockchain"
                  alt="MDM"
                />
                <strong>{selectedToChain?.name}</strong>
                {selectedToChain?.symbol === `KS_BESU` &&
                  transactionProgress.step === `4-3` &&
                  transactionProgress.status !== `success` && (
                    <button
                      className="t-btn"
                      onClick={() => {
                        setIsShowWithdrawApproval(true);
                      }}
                      disabled={isConfirm}
                    >
                      Confirm
                    </button>
                  )}
              </div>
            </div>
            <div className="exchange-detail">
              {transactionProgress.step >= `4-2` && (
                <ul className="caution">
                  <li>
                    KSTADIUM Bridge is converted based on hash time lock
                    technology.
                  </li>
                  <li>
                    From, To chain&#39;s Explorer can monitor the progress, and
                    if the conversion is not processed, refunds can be made from
                    {` `}
                    {fromCurrentTimelock} &#40;UTC&#41;.
                  </li>
                  <li>
                    If you need a refund, please visit the history menu and
                    proceed with the refund.
                  </li>
                </ul>
              )}
              <div>
                <span>From chain Tx Hash </span>
                {fromTransactionHash &&
                fromTransactionHash !==
                  `0x0000000000000000000000000000000000000000000000000000000000000000` ? (
                  <a
                    href={
                      fromChain
                        ? fromChain.explorerUrl +
                          fromChain.explorerTxPrefix +
                          fromTransactionHash
                        : `#`
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    <strong>
                      {fromTransactionHash}
                      <i data-icon="window" />
                    </strong>
                  </a>
                ) : (
                  <strong style={{ color: `#888` }}>No Tx Hash</strong>
                )}
              </div>
              <div className="current-step">
                <span>To chain Tx Hash </span>
                {txCurrentHistory?.toChainHtlcState === `withdrawn` ? (
                  <a
                    href={
                      toChain
                        ? toChain.explorerUrl +
                          toChain.explorerTxPrefix +
                          toTransactionHash
                        : `#`
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    <strong>
                      {toTransactionHash}
                      <i data-icon="window" />
                    </strong>
                  </a>
                ) : (
                  <strong style={{ color: `#888` }}>No Tx Hash</strong>
                )}
              </div>
            </div>
            <div className="ux-hint">
              <p>
                <i data-icon="alert" />
                If you can&#39;t find the Tx hash in Explorer, please try again
                in one to two minutes.
              </p>
            </div>
          </div>
        </div>
      </main>
      <ModalNewContractApproval
        isShow={isShowNewContractApproval}
        onRequestClose={() => setIsShowNewContractApproval(false)}
        onAgree={() => {
          dispatch(setIsConfirm(true));
          onRequestConfirmNewContract();
        }}
      />
      <ModalWithdrawApproval
        isShow={isShowWithdrawApproval}
        onRequestClose={() => setIsShowWithdrawApproval(false)}
        onAgree={() => {
          dispatch(setIsConfirm(true));
          onRequestConfirmWithdraw();
        }}
      />
      <ModalToChainTimeout
        isShow={txCurrentHistory?.toChainHtlcState === `expired`}
        onRequestClose={() => window.location.replace(`/bridge`)}
      />
      <ModalTransactionComplete
        isShow={
          !txHasError && txCurrentHistory?.toChainHtlcState === `withdrawn`
        }
        chainSymbol={txCurrentHistory?.toChainSymbol}
        txHash={toTransactionHash}
        onRequestClose={() => window.location.replace(`/bridge`)}
      />
      <ModalTransactionFailed
        isShow={txHasError}
        chainSymbol={txCurrentHistory?.toChainSymbol}
        txHash={toTransactionHash}
        onRequestClose={() => window.location.replace(`/bridge`)}
      />
      <ModalApproval
        isShow={isShowModalApproval}
        onRequestClose={() => {
          setIsShowModalApproval(false);
          dispatch(setTxHasError(true));
        }}
        handleClickApprove={() => {
          gndChainApproval();
          setIsShowModalApproval(false);
        }}
      />
      <ModalApprovalFailed
        isShow={isShowModalApprovalFailed}
        onRequestClose={() => window.location.replace(`/bridge`)}
      />

      {/* <ModalOTPFailed */}
      {/*  isShow={isShowModalOTP} */}
      {/*  onRequestClose={() => setIsShowModalOTP(false)} */}
      {/*  onClickSubmit={(otpConfirm) => { */}
      {/*    if (otpConfirm) { */}
      {/*      setIsShowNewContractApproval(true); */}
      {/*    } */}
      {/*    setIsShowModalOTP(false); */}
      {/*  }} */}
      {/* /> */}
      {/* <ModalOTPFailed */}
      {/*  isShow={isShowModalOTP} */}
      {/*  onRequestClose={() => setIsShowModalOTP(false)} */}
      {/*  onClickSubmit={(otpConfirm) => { */}
      {/*    if (otpConfirm) { */}
      {/*      setIsShowNewContractApproval(true); */}
      {/*    } */}
      {/*    setIsShowModalOTP(false); */}
      {/*  }} */}
      {/* /> */}
      {/* <ModalTransactionDelay isShow={isConfirm} /> */}
    </OTPProvider>
  );
};

export default TransactionProgress;
